import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Monitor, Link, Unlink, Trash2, Copy, MonitorX } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { screenManagementService } from '../../services/screenManagementService';
import { eventsService } from '../../services/firestoreService';
import CreateScreenModal from './CreateScreenModal';
import LinkScreenModal from './LinkScreenModal';
import ConfirmDialog from '../Common/ConfirmDialog';

const ScreenManagement = () => {
  const { user } = useAuth();
  const [screens, setScreens] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    linked: 0,
    unlinked: 0
  });
  const [loading, setLoading] = useState(true);
  const [onlineFilter, setOnlineFilter] = useState('all'); // all | online | offline
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, action: null, screenId: null, loading: false, title: '', message: '', variant: 'danger' });
  // Carga de datos (definido arriba para evitar no-use-before-define en el effect)
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [screensData, eventsData, statsData] = await Promise.all([
        screenManagementService.getOrganizerScreens(user?.uid),
        eventsService.getOrganizerEvents(user?.uid),
        screenManagementService.getScreenStats(user?.uid)
      ]);
      
      // Ordenar por último heartbeat (más recientes primero) y calcular online
      const getTs = (t) => t?.toDate ? t.toDate().getTime() : (t ? new Date(t).getTime() : 0);
      screensData.sort((a,b) => (getTs(b.lastHeartbeat) || getTs(b.updatedAt) || 0) - (getTs(a.lastHeartbeat) || getTs(a.updatedAt) || 0));
      const onlineCount = screensData.filter(s => s.clientLinked && (Date.now() - getTs(s.lastHeartbeat)) < 90000).length;

      setScreens(screensData);
      setEvents(eventsData);
      setStats({ ...statsData, online: onlineCount });
    } catch (error) {
      console.error('Error loading screen data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  // eslint-disable-next-line no-use-before-define
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  // loadData definido arriba

  const handleCreateScreen = async (screenData) => {
    try {
      const created = await screenManagementService.createScreen(screenData, user.uid);
      setShowCreateModal(false);
      toast.success(`Pantalla "${created.name}" creada`);
      loadData();
    } catch (error) {
      console.error('Error creating screen:', error);
      toast.error('Error al crear la pantalla');
    }
  };

  const handleLinkScreen = async (screenId, eventId) => {
    try {
      await screenManagementService.linkScreenToEvent(screenId, eventId);
      setShowLinkModal(false);
      toast.success('Pantalla vinculada al evento');
      loadData();
    } catch (error) {
      console.error('Error linking screen:', error);
      toast.error('Error al vincular la pantalla');
    }
  };

  const confirmUnlink = (screen) => {
    const eventName = screen?.eventId ? getEventName(screen.eventId) : null;
    setConfirmState({
      open: true,
      action: 'unlink',
      screenId: screen.id,
      loading: false,
      title: 'Salir del evento',
      message: eventName
        ? `Esta pantalla se saldrá del evento "${eventName}". ¿Deseas continuar?`
        : 'Esta pantalla se saldrá del evento actual. ¿Deseas continuar?',
      variant: 'warning'
    });
  };

  const confirmDelete = (screenId) => {
    setConfirmState({
      open: true,
      action: 'delete',
      screenId,
      loading: false,
      title: 'Eliminar pantalla',
      message: 'Esta acción no se puede deshacer. ¿Eliminar pantalla?',
      variant: 'danger'
    });
  };

  const confirmReset = (screenId) => {
    const sc = screens.find(s => s.id === screenId);
    if (!sc?.clientLinked) {
      toast.error('La pantalla no está vinculada a una Screen');
      return;
    }
    setConfirmState({
      open: true,
      action: 'reset',
      screenId,
      loading: false,
      title: 'Desenlazar pantalla',
      message: 'Al desenlazar la pantalla, volverás a solicitar un código de vinculación. ¿Deseas continuar?',
      variant: 'warning'
    });
  };

  const handleConfirm = async () => {
    const { action, screenId } = confirmState;
    try {
      setConfirmState(s => ({ ...s, loading: true }));
      if (action === 'unlink') {
        await screenManagementService.unlinkScreenFromEvent(screenId);
        toast.success('Pantalla desvinculada');
      } else if (action === 'delete') {
        await screenManagementService.deleteScreen(screenId);
        toast.success('Pantalla eliminada');
      } else if (action === 'reset') {
        // Verificar que la pantalla siga vinculada a una Screen
        const latest = await screenManagementService.getScreenById(screenId);
        if (!latest?.clientLinked) {
          toast.error('La pantalla no está vinculada a una Screen');
        } else {
          await screenManagementService.forceResetScreen(screenId);
          toast.success('Se solicitó el reinicio del dispositivo');
        }
      }
      setConfirmState({ open: false, action: null, screenId: null, loading: false });
      loadData();
    } catch (error) {
      console.error('Screen action error:', error);
      toast.error('Ocurrió un error al procesar la acción');
      setConfirmState(s => ({ ...s, loading: false }));
    }
  };

  const handleCancel = () => setConfirmState({ open: false, action: null, screenId: null, loading: false });

  const copyScreenCode = (code) => {
    navigator.clipboard.writeText(code);
    // Aquí podrías mostrar un toast de confirmación
  };

  const getEventName = (eventId) => {
    const event = events.find(e => (e.eventId || e.id) === eventId);
    return event ? event.name : 'Evento no encontrado';
  };

  // Estado visual ahora se basa en conexión (online/offline)

  const isOnline = (screen) => {
    if (!screen?.clientLinked) return false;
    const hb = screen?.lastHeartbeat;
    const ts = hb?.toDate ? hb.toDate().getTime() : (hb ? new Date(hb).getTime() : 0);
    return ts && (Date.now() - ts) < 90_000; // 90s
  };

  const formatRelative = (date) => {
    if (!date) return '—';
    const d = date.toDate ? date.toDate() : new Date(date);
    const diff = Math.max(0, Date.now() - d.getTime());
    const s = Math.floor(diff/1000);
    if (s < 60) return `hace ${s}s`;
    const m = Math.floor(s/60);
    if (m < 60) return `hace ${m}m`;
    const h = Math.floor(m/60);
    return `hace ${h}h`;
  };

  const filteredScreens = useMemo(() => {
    if (onlineFilter === 'online') return screens.filter(isOnline);
    if (onlineFilter === 'offline') return screens.filter(s => !isOnline(s));
    return screens;
  }, [screens, onlineFilter]);

  const offlineMinutes = (screen) => {
    const hb = screen?.lastHeartbeat;
    const ts = hb?.toDate ? hb.toDate().getTime() : (hb ? new Date(hb).getTime() : 0);
    if (!ts) return Infinity;
    return Math.floor((Date.now() - ts) / 60000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Cargando pantallas...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Pantallas</h1>
        <p className="text-gray-600">Administra las pantallas para tus eventos</p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Pantallas</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600">Activas</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-gray-600">{stats.inactive}</div>
          <div className="text-sm text-gray-600">Inactivas</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{stats.linked}</div>
          <div className="text-sm text-gray-600">Vinculadas</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.unlinked}</div>
          <div className="text-sm text-gray-600">Disponibles</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600">{stats.online || 0}</div>
          <div className="text-sm text-gray-600">En línea</div>
        </div>
      </div>

      {/* Acciones y filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Pantallas</h2>
        <div className="flex items-center gap-3">
          <select
            value={onlineFilter}
            onChange={(e) => setOnlineFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            title="Filtrar por estado de conexión"
          >
            <option value="all">Todas</option>
            <option value="online">Solo en línea</option>
            <option value="offline">Solo offline</option>
          </select>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <Plus size={16} className="mr-2" />
            Nueva Pantalla
          </button>
        </div>
      </div>

      {/* Lista de Pantallas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredScreens.map((screen) => (
          <div
            key={screen.id}
            className="card p-6"
          >
            {/* Header de la Pantalla */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Monitor size={20} className="text-blue-600" />
                <h3 className="font-medium text-gray-900">{screen.name}</h3>
              </div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isOnline(screen) ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                {isOnline(screen) ? 'En línea' : 'Offline'}
              </span>
            </div>

            {/* Información de la Pantalla */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Código:</span>
                <div className="flex items-center space-x-2">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                    {screen.code}
                  </code>
                  <button
                    onClick={() => copyScreenCode(screen.code)}
                    className="text-gray-400 hover:text-gray-600"
                    title="Copiar código"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Ubicación:</span>
                <span className="text-sm text-gray-900">{screen.location}</span>
              </div>

              {screen.eventId && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Evento:</span>
                  <span className="text-sm text-blue-600 font-medium">
                    {getEventName(screen.eventId)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Último pulso:</span>
                <span className={`text-sm ${!isOnline(screen) && offlineMinutes(screen) >= 10 ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                  {formatRelative(screen.lastHeartbeat)}
                </span>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex space-x-2">
              {screen.eventId ? (
                <button
                  type="button"
                  onClick={() => confirmUnlink(screen)}
                  className="btn-secondary flex-1 text-sm"
                  title="Desvincular del evento"
                >
                  <Unlink size={14} className="mr-1" />
                  Desvincular
                </button>
              ) : (
                <button
                  onClick={() => {
                    setSelectedScreen(screen);
                    setShowLinkModal(true);
                  }}
                  className="btn-primary flex-1 text-sm"
                  title="Vincular a un evento"
                >
                  <Link size={14} className="mr-1" />
                  Vincular
                </button>
              )}
              
              <button
                onClick={() => confirmDelete(screen.id)}
                className="btn-danger px-3"
                title="Eliminar pantalla"
              >
                <Trash2 size={14} />
              </button>
              <button
                onClick={() => confirmReset(screen.id)}
                className="btn-secondary px-3 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!screen.clientLinked ? 'No vinculado a una Screen' : 'Desvincular dispositivo (volver a pedir código)'}
                disabled={!screen.clientLinked}
              >
                <MonitorX size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {screens.length === 0 && (
        <div className="text-center py-12">
          <Monitor size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pantallas</h3>
          <p className="text-gray-600 mb-4">Crea tu primera pantalla para comenzar</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <Plus size={16} className="mr-2" />
            Crear Pantalla
          </button>
        </div>
      )}

      {/* Modales */}
      <CreateScreenModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateScreen}
      />

      <LinkScreenModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onSubmit={handleLinkScreen}
        screen={selectedScreen}
        events={(() => {
          const allowed = events.filter(e => ['upcoming', 'active', 'draft'].includes(e.status));
          return allowed.length > 0 ? allowed : events;
        })()}
      />

      <ConfirmDialog
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        loading={confirmState.loading}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmText={confirmState.action === 'delete' ? 'Eliminar' : 'Desvincular'}
      />
    </div>
  );
};

export default ScreenManagement;
